import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Skeleton } from "../components/ui/skeleton";
import { Mail, Smartphone, Monitor, Send, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { StatusPill } from "../components/StatusPills";

function FrameToggle({ mode, setMode }) {
  return (
    <div className="inline-flex rounded-md border border-border overflow-hidden" data-testid="email-frame-toggle">
      <button onClick={() => setMode("desktop")} className={`px-3 py-1.5 text-xs ${mode === "desktop" ? "bg-muted text-foreground" : "text-muted-foreground"}`}><Monitor size={14} className="inline mr-1" />Desktop</button>
      <button onClick={() => setMode("mobile")} className={`px-3 py-1.5 text-xs ${mode === "mobile" ? "bg-muted text-foreground" : "text-muted-foreground"}`}><Smartphone size={14} className="inline mr-1" />Mobile</button>
    </div>
  );
}

function EmailPreview({ html, mode }) {
  const size = mode === "mobile" ? { width: 390, height: 740 } : { width: 820, height: 640 };
  return (
    <div className="rounded-xl border border-border bg-black/20 p-3 overflow-auto" data-testid="email-preview-frame">
      <div className="mx-auto" style={{ width: size.width }}>
        <iframe
          title="email preview"
          srcDoc={html}
          style={{ width: size.width, height: size.height, border: "1px solid hsl(var(--border))", borderRadius: 8, background: "#0b1220" }}
        />
      </div>
    </div>
  );
}

export default function Recap() {
  const [kind, setKind] = useState("daily");
  const [notes, setNotes] = useState("");
  const [wins, setWins] = useState("");
  const [risks, setRisks] = useState("");
  const [actions, setActions] = useState("");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [mode, setMode] = useState("desktop");
  const [logs, setLogs] = useState([]);
  const [testTo, setTestTo] = useState("");
  const [defaultTo, setDefaultTo] = useState([]);

  const buildPreview = async () => {
    setLoading(true);
    try {
      const url = `/recap/preview/${kind}`;
      const body = { notes };
      if (kind === "weekly") Object.assign(body, { wins, risks, action_items: actions });
      const { data } = await api.post(url, body);
      setPreview(data);
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  };

  const loadLogs = async () => {
    const { data } = await api.get("/recap/logs", { params: { limit: 20 } });
    setLogs(data.logs || []);
  };

  const loadSettings = async () => {
    const { data } = await api.get("/settings/email");
    setDefaultTo(data.recipients || []);
    if ((data.recipients || []).length && !testTo) setTestTo(data.recipients[0]);
  };

  useEffect(() => { buildPreview(); loadLogs(); loadSettings(); /* eslint-disable-next-line */ }, [kind]);

  const sendNow = async (test) => {
    setSending(true);
    try {
      const to = test ? (testTo ? [testTo] : []) : defaultTo;
      const body = { kind, to, notes, test };
      const { data } = await api.post("/recap/send", body);
      if (data.status === "sent") toast.success(`Sent · ${data.email_id}`);
      else toast.error(`Send failed: ${data.error || "unknown"}`);
      loadLogs();
    } catch (e) { toast.error(e.message); }
    finally { setSending(false); }
  };

  return (
    <div className="space-y-6" data-testid="page-recap">
      <div>
        <h1 className="font-display text-2xl font-semibold">Recap Emails</h1>
        <p className="text-sm text-muted-foreground">Preview, edit, and send the morning and Monday executive recaps via Resend.</p>
      </div>

      <Tabs value={kind} onValueChange={setKind}>
        <TabsList>
          <TabsTrigger value="daily" data-testid="recap-tab-daily">Daily Morning Recap</TabsTrigger>
          <TabsTrigger value="weekly" data-testid="recap-tab-weekly">Monday Weekly Recap</TabsTrigger>
        </TabsList>
        <TabsContent value={kind} className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
            <Card className="lg:col-span-4">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-sm font-medium"><Mail size={16} /> Compose</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <div className="text-xs text-muted-foreground mb-1">Management notes</div>
                  <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional note shown in the email" data-testid="recap-notes" />
                </div>
                {kind === "weekly" && (
                  <>
                    <div><div className="text-xs text-muted-foreground mb-1">Wins</div><Textarea value={wins} onChange={(e) => setWins(e.target.value)} data-testid="recap-wins" /></div>
                    <div><div className="text-xs text-muted-foreground mb-1">Risks</div><Textarea value={risks} onChange={(e) => setRisks(e.target.value)} data-testid="recap-risks" /></div>
                    <div><div className="text-xs text-muted-foreground mb-1">Action items</div><Textarea value={actions} onChange={(e) => setActions(e.target.value)} data-testid="recap-actions" /></div>
                  </>
                )}
                <Button onClick={buildPreview} variant="outline" className="w-full" data-testid="recap-rebuild"><RefreshCw size={14} className="mr-2" />Rebuild preview</Button>
                <div className="pt-3 border-t border-border space-y-2">
                  <div className="text-xs text-muted-foreground">Test send to</div>
                  <Input value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="your@email.com" data-testid="recap-test-to" />
                  <Button disabled={sending} className="w-full" variant="outline" onClick={() => sendNow(true)} data-testid="recap-send-test"><Send size={14} className="mr-2" />Send Test</Button>
                  <Button disabled={sending || !defaultTo.length} className="w-full" onClick={() => sendNow(false)} data-testid="recap-send-now"><Send size={14} className="mr-2" />Send to {defaultTo.length} recipient{defaultTo.length === 1 ? "" : "s"}</Button>
                  {!defaultTo.length && <div className="text-xs text-[hsl(var(--warning))]">Configure recipients in Settings first.</div>}
                </div>
              </CardContent>
            </Card>

            <Card className="lg:col-span-8">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-sm font-medium">{preview?.subject || "Preview"}</CardTitle>
                  <div className="text-xs text-muted-foreground">Rendered HTML via Resend template</div>
                </div>
                <FrameToggle mode={mode} setMode={setMode} />
              </CardHeader>
              <CardContent>
                {loading ? <Skeleton className="h-[640px] w-full" /> : preview ? <EmailPreview html={preview.html} mode={mode} /> : null}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      <Card>
        <CardHeader><CardTitle className="text-sm font-medium">Send history</CardTitle></CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>When</TableHead>
                <TableHead>Kind</TableHead>
                <TableHead>Subject</TableHead>
                <TableHead>To</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Email ID</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {logs.map((l) => (
                <TableRow key={l.id}>
                  <TableCell className="text-xs text-muted-foreground">{l.completed_at ? new Date(l.completed_at).toLocaleString() : "—"}</TableCell>
                  <TableCell className="text-xs">{l.kind}</TableCell>
                  <TableCell className="text-xs truncate max-w-[280px]">{l.subject}</TableCell>
                  <TableCell className="text-xs">{(l.to || []).join(", ")}</TableCell>
                  <TableCell className="text-xs"><span className={l.status === "sent" ? "delta-positive" : "delta-negative"}>{l.status}</span></TableCell>
                  <TableCell className="text-[11px] font-mono text-muted-foreground">{l.email_id || l.error?.slice(0, 40) || "–"}</TableCell>
                </TableRow>
              ))}
              {!logs.length && <TableRow><TableCell colSpan={6} className="text-center py-6 text-muted-foreground">No sends yet</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
